/**
 * Attachment List Component
 * Displays list of attachments with download and delete actions
 */

import {
  Paper,
  Group,
  Stack,
  Text,
  ActionIcon,
  Tooltip,
  Badge,
  LoadingOverlay,
} from "@mantine/core";
import {
  IconDownload,
  IconTrash,
  IconPhoto,
  IconFileText,
  IconFileTypePdf,
  IconFileTypeDocx,
  IconFileTypeXls,
  IconAlertCircle,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { format } from "date-fns";
import {
  useAttachments,
  useDeleteAttachment,
  downloadAttachment,
} from "../../hooks/useAttachments";
import { AuditFindingAttachment } from "../../types/audit";

function isWithin24Hours(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < 24 * 60 * 60 * 1000;
}

interface AttachmentListProps {
  findingId: string;
  canDelete?: boolean; // Whether user can delete attachments (auditor only, within 24 hours)
}

export function AttachmentList({
  findingId,
  canDelete = false,
}: AttachmentListProps) {
  const { data: attachments = [], isLoading } = useAttachments(findingId);
  const deleteMutation = useDeleteAttachment(findingId);

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <IconPhoto size={24} color="var(--mantine-color-blue-6)" />;
    }
    if (fileType === "application/pdf") {
      return <IconFileTypePdf size={24} color="var(--mantine-color-red-6)" />;
    }
    if (
      fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <IconFileTypeDocx size={24} color="var(--mantine-color-blue-7)" />;
    }
    if (
      fileType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return <IconFileTypeXls size={24} color="var(--mantine-color-green-6)" />;
    }
    return <IconFileText size={24} color="var(--mantine-color-gray-6)" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = async (attachment: AuditFindingAttachment) => {
    try {
      await downloadAttachment(findingId, attachment.id, attachment.file_name);
    } catch {
      notifications.show({
        title: "Download Failed",
        message: "Failed to download attachment",
        color: "red",
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleDelete = (attachment: AuditFindingAttachment) => {
    modals.openConfirmModal({
      title: "Delete Attachment",
      children: (
        <Text size="sm">
          Are you sure you want to delete{" "}
          <strong>{attachment.file_name}</strong>? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(attachment.id);

          notifications.show({
            title: "Deleted",
            message: "Attachment has been deleted",
            color: "green",
          });
        } catch {
          // error toast comes from the global mutation handler (US-081)
        }
      },
    });
  };

  if (isLoading) {
    return (
      <Paper p="md" withBorder pos="relative" style={{ minHeight: 100 }}>
        <LoadingOverlay visible />
      </Paper>
    );
  }

  if (attachments.length === 0) {
    return (
      <Paper
        p="md"
        withBorder
        style={{ backgroundColor: "var(--mantine-color-gray-light)" }}
      >
        <Text size="sm" c="dimmed" ta="center">
          No attachments yet
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="xs">
      {attachments.map((attachment) => {
        const canDeleteThis =
          canDelete && isWithin24Hours(attachment.created_at);

        return (
          <Paper key={attachment.id} p="sm" withBorder>
            <Group justify="space-between" wrap="nowrap">
              <Group gap="md" style={{ flex: 1, minWidth: 0 }}>
                {getFileIcon(attachment.file_type)}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} size="sm" truncate>
                    {attachment.file_name}
                  </Text>

                  <Group gap="xs" wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      {formatFileSize(attachment.file_size)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      •
                    </Text>
                    <Text size="xs" c="dimmed">
                      {format(
                        new Date(attachment.created_at),
                        "MMM d, yyyy h:mm a",
                      )}
                    </Text>
                  </Group>

                  {attachment.description && (
                    <Text size="xs" c="dimmed" mt={4} lineClamp={2}>
                      {attachment.description}
                    </Text>
                  )}

                  {canDeleteThis && (
                    <Badge size="xs" color="orange" variant="light" mt={4}>
                      Can delete (within 24h)
                    </Badge>
                  )}
                </div>
              </Group>

              <Group gap="xs" wrap="nowrap">
                <Tooltip label="Download">
                  <ActionIcon
                    variant="subtle"
                    color="blue"
                    onClick={() => handleDownload(attachment)}
                    size="lg"
                  >
                    <IconDownload size={18} />
                  </ActionIcon>
                </Tooltip>

                {canDeleteThis && (
                  <Tooltip label="Delete">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleDelete(attachment)}
                      loading={deleteMutation.isPending}
                      size="lg"
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}
